/**
 * Enhanced Idle Animation System
 * Provides varied breathing patterns and micro-expression variety
 * Creates more lifelike idle behaviors with randomized timing and intensity
 * Nuamedia Solutions Pty Ltd
 */

// ============================================
// Types
// ============================================

export interface BlendshapeFrame {
  time: number;
  blendshapes: Record<string, number>;
}

export type BreathingPattern = 'relaxed' | 'attentive' | 'deep' | 'subtle';
export type MicroExpressionType =
  | 'blink'
  | 'doubleBlink'
  | 'halfBlink'
  | 'browRaise'
  | 'browFurrow'
  | 'asymmetricSmile'
  | 'lipPurse'
  | 'noseTwitch'
  | 'eyeShift'
  | 'headTilt';

export interface EnhancedIdleConfig {
  enableBreathing?: boolean;
  enableMicroExpressions?: boolean;
  enableNaturalBlinks?: boolean;
  breathingPattern?: BreathingPattern;
  microExpressionFrequency?: number; // seconds between micro-expressions
  blinkFrequencyMin?: number; // min seconds between blinks
  blinkFrequencyMax?: number; // max seconds between blinks
  intensityMultiplier?: number; // 0.0 - 2.0, default 1.0
}

export interface IdleAnimationState {
  isActive: boolean;
  currentBreathPhase: number; // 0.0 - 1.0 through breath cycle
  currentMicroExpression: MicroExpressionType | null;
  lastBlinkTime: number;
  lastMicroExpressionTime: number;
  breathingIntensity: number;
}

// ============================================
// Breathing Patterns
// ============================================

/**
 * Different breathing patterns with varying rhythm and intensity
 * Each pattern defines: cycle duration, intensity curve, and affected blendshapes
 */
export const BREATHING_PATTERNS: Record<BreathingPattern, {
  cycleDuration: number; // seconds
  inhaleRatio: number; // portion of cycle for inhale (0-1)
  holdRatio: number; // portion of cycle for hold at peak
  peakIntensity: number;
  blendshapes: Record<string, { min: number; max: number }>;
}> = {
  relaxed: {
    cycleDuration: 4.0,
    inhaleRatio: 0.35,
    holdRatio: 0.1,
    peakIntensity: 0.6,
    blendshapes: {
      cheekPuff: { min: 0.005, max: 0.025 },
      jawOpen: { min: 0.002, max: 0.008 },
      mouthClose: { min: 0.005, max: 0.002 }
    }
  },
  attentive: {
    cycleDuration: 3.2,
    inhaleRatio: 0.4,
    holdRatio: 0.15,
    peakIntensity: 0.75,
    blendshapes: {
      cheekPuff: { min: 0.008, max: 0.035 },
      jawOpen: { min: 0.003, max: 0.012 },
      mouthClose: { min: 0.003, max: 0.001 },
      browInnerUp: { min: 0.005, max: 0.015 }
    }
  },
  deep: {
    cycleDuration: 5.5,
    inhaleRatio: 0.3,
    holdRatio: 0.12,
    peakIntensity: 0.9,
    blendshapes: {
      cheekPuff: { min: 0.01, max: 0.045 },
      jawOpen: { min: 0.005, max: 0.018 },
      mouthClose: { min: 0.002, max: 0.0 },
      noseSneerLeft: { min: 0.0, max: 0.008 },
      noseSneerRight: { min: 0.0, max: 0.008 }
    }
  },
  subtle: {
    cycleDuration: 4.5,
    inhaleRatio: 0.35,
    holdRatio: 0.08,
    peakIntensity: 0.4,
    blendshapes: {
      cheekPuff: { min: 0.003, max: 0.015 },
      jawOpen: { min: 0.001, max: 0.005 },
      mouthClose: { min: 0.003, max: 0.003 }
    }
  }
};

// ============================================
// Micro-Expression Library
// ============================================

/**
 * Library of subtle micro-expressions for variety
 * Each has timing, blendshapes, and probability weight
 */
export const MICRO_EXPRESSIONS: Record<MicroExpressionType, {
  duration: number; // seconds
  probability: number; // relative weight for random selection
  frames: BlendshapeFrame[];
}> = {
  blink: {
    duration: 0.15,
    probability: 35,
    frames: [
      { time: 0, blendshapes: { eyeBlinkLeft: 0, eyeBlinkRight: 0 } },
      { time: 0.03, blendshapes: { eyeBlinkLeft: 0.7, eyeBlinkRight: 0.7 } },
      { time: 0.06, blendshapes: { eyeBlinkLeft: 1.0, eyeBlinkRight: 1.0 } },
      { time: 0.09, blendshapes: { eyeBlinkLeft: 1.0, eyeBlinkRight: 1.0 } },
      { time: 0.12, blendshapes: { eyeBlinkLeft: 0.5, eyeBlinkRight: 0.5 } },
      { time: 0.15, blendshapes: { eyeBlinkLeft: 0, eyeBlinkRight: 0 } }
    ]
  },
  doubleBlink: {
    duration: 0.35,
    probability: 8,
    frames: [
      { time: 0, blendshapes: { eyeBlinkLeft: 0, eyeBlinkRight: 0 } },
      { time: 0.03, blendshapes: { eyeBlinkLeft: 0.8, eyeBlinkRight: 0.8 } },
      { time: 0.06, blendshapes: { eyeBlinkLeft: 1.0, eyeBlinkRight: 1.0 } },
      { time: 0.1, blendshapes: { eyeBlinkLeft: 0.2, eyeBlinkRight: 0.2 } },
      { time: 0.15, blendshapes: { eyeBlinkLeft: 0, eyeBlinkRight: 0 } },
      { time: 0.2, blendshapes: { eyeBlinkLeft: 0.7, eyeBlinkRight: 0.7 } },
      { time: 0.25, blendshapes: { eyeBlinkLeft: 1.0, eyeBlinkRight: 1.0 } },
      { time: 0.3, blendshapes: { eyeBlinkLeft: 0.4, eyeBlinkRight: 0.4 } },
      { time: 0.35, blendshapes: { eyeBlinkLeft: 0, eyeBlinkRight: 0 } }
    ]
  },
  halfBlink: {
    duration: 0.12,
    probability: 12,
    frames: [
      { time: 0, blendshapes: { eyeBlinkLeft: 0, eyeBlinkRight: 0 } },
      { time: 0.04, blendshapes: { eyeBlinkLeft: 0.4, eyeBlinkRight: 0.4 } },
      { time: 0.08, blendshapes: { eyeBlinkLeft: 0.5, eyeBlinkRight: 0.5 } },
      { time: 0.12, blendshapes: { eyeBlinkLeft: 0, eyeBlinkRight: 0 } }
    ]
  },
  browRaise: {
    duration: 0.6,
    probability: 10,
    frames: [
      { time: 0, blendshapes: { browInnerUp: 0, browOuterUpLeft: 0, browOuterUpRight: 0 } },
      { time: 0.15, blendshapes: { browInnerUp: 0.2, browOuterUpLeft: 0.15, browOuterUpRight: 0.15 } },
      { time: 0.3, blendshapes: { browInnerUp: 0.25, browOuterUpLeft: 0.18, browOuterUpRight: 0.18 } },
      { time: 0.45, blendshapes: { browInnerUp: 0.12, browOuterUpLeft: 0.08, browOuterUpRight: 0.08 } },
      { time: 0.6, blendshapes: { browInnerUp: 0, browOuterUpLeft: 0, browOuterUpRight: 0 } }
    ]
  },
  browFurrow: {
    duration: 0.5,
    probability: 8,
    frames: [
      { time: 0, blendshapes: { browDownLeft: 0, browDownRight: 0 } },
      { time: 0.12, blendshapes: { browDownLeft: 0.2, browDownRight: 0.2 } },
      { time: 0.25, blendshapes: { browDownLeft: 0.25, browDownRight: 0.25 } },
      { time: 0.38, blendshapes: { browDownLeft: 0.1, browDownRight: 0.1 } },
      { time: 0.5, blendshapes: { browDownLeft: 0, browDownRight: 0 } }
    ]
  },
  asymmetricSmile: {
    duration: 0.8,
    probability: 12,
    frames: [
      { time: 0, blendshapes: { mouthSmileLeft: 0.02, mouthSmileRight: 0.02 } },
      { time: 0.2, blendshapes: { mouthSmileLeft: 0.15, mouthSmileRight: 0.08 } },
      { time: 0.4, blendshapes: { mouthSmileLeft: 0.18, mouthSmileRight: 0.1 } },
      { time: 0.6, blendshapes: { mouthSmileLeft: 0.1, mouthSmileRight: 0.06 } },
      { time: 0.8, blendshapes: { mouthSmileLeft: 0.02, mouthSmileRight: 0.02 } }
    ]
  },
  lipPurse: {
    duration: 0.4,
    probability: 5,
    frames: [
      { time: 0, blendshapes: { mouthPucker: 0, mouthFunnel: 0 } },
      { time: 0.1, blendshapes: { mouthPucker: 0.15, mouthFunnel: 0.08 } },
      { time: 0.2, blendshapes: { mouthPucker: 0.2, mouthFunnel: 0.1 } },
      { time: 0.3, blendshapes: { mouthPucker: 0.1, mouthFunnel: 0.05 } },
      { time: 0.4, blendshapes: { mouthPucker: 0, mouthFunnel: 0 } }
    ]
  },
  noseTwitch: {
    duration: 0.25,
    probability: 4,
    frames: [
      { time: 0, blendshapes: { noseSneerLeft: 0, noseSneerRight: 0 } },
      { time: 0.08, blendshapes: { noseSneerLeft: 0.2, noseSneerRight: 0.18 } },
      { time: 0.15, blendshapes: { noseSneerLeft: 0.25, noseSneerRight: 0.22 } },
      { time: 0.25, blendshapes: { noseSneerLeft: 0, noseSneerRight: 0 } }
    ]
  },
  eyeShift: {
    duration: 0.5,
    probability: 6,
    frames: [
      { time: 0, blendshapes: { eyeLookOutLeft: 0, eyeLookInRight: 0 } },
      { time: 0.15, blendshapes: { eyeLookOutLeft: 0.15, eyeLookInRight: 0.15 } },
      { time: 0.35, blendshapes: { eyeLookOutLeft: 0.12, eyeLookInRight: 0.12 } },
      { time: 0.5, blendshapes: { eyeLookOutLeft: 0, eyeLookInRight: 0 } }
    ]
  },
  headTilt: {
    duration: 0.7,
    probability: 3,
    frames: [
      // Head tilt is handled via bone rotation, these are supporting facial cues
      { time: 0, blendshapes: { browInnerUp: 0.01, mouthSmileLeft: 0.02, mouthSmileRight: 0.02 } },
      { time: 0.2, blendshapes: { browInnerUp: 0.08, mouthSmileLeft: 0.04, mouthSmileRight: 0.02 } },
      { time: 0.4, blendshapes: { browInnerUp: 0.1, mouthSmileLeft: 0.05, mouthSmileRight: 0.025 } },
      { time: 0.55, blendshapes: { browInnerUp: 0.05, mouthSmileLeft: 0.03, mouthSmileRight: 0.02 } },
      { time: 0.7, blendshapes: { browInnerUp: 0.01, mouthSmileLeft: 0.02, mouthSmileRight: 0.02 } }
    ]
  }
};

// ============================================
// Enhanced Idle Animation Controller
// ============================================

export class EnhancedIdleController {
  private config: Required<EnhancedIdleConfig>;
  private state: IdleAnimationState;
  private animationFrameId: number | null = null;
  private startTime: number = 0;
  private applyBlendshapes: ((blendshapes: Record<string, number>) => void) | null = null;

  // Breathing state
  private currentBreathingPattern: BreathingPattern;
  private breathCycleStart: number = 0;
  private breathingVariation: number = 0; // Random variation per cycle

  // Micro-expression state
  private currentMicroExpression: {
    type: MicroExpressionType;
    startTime: number;
    frames: BlendshapeFrame[];
  } | null = null;

  // Blink state
  private nextBlinkTime: number = 0;
  private isBlinking: boolean = false;
  private blinkStartTime: number = 0;

  constructor(config: EnhancedIdleConfig = {}) {
    this.config = {
      enableBreathing: config.enableBreathing ?? true,
      enableMicroExpressions: config.enableMicroExpressions ?? true,
      enableNaturalBlinks: config.enableNaturalBlinks ?? true,
      breathingPattern: config.breathingPattern ?? 'relaxed',
      microExpressionFrequency: config.microExpressionFrequency ?? 8,
      blinkFrequencyMin: config.blinkFrequencyMin ?? 2.5,
      blinkFrequencyMax: config.blinkFrequencyMax ?? 6,
      intensityMultiplier: config.intensityMultiplier ?? 1.0
    };

    this.currentBreathingPattern = this.config.breathingPattern;

    this.state = {
      isActive: false,
      currentBreathPhase: 0,
      currentMicroExpression: null,
      lastBlinkTime: 0,
      lastMicroExpressionTime: 0,
      breathingIntensity: 1.0
    };
  }

  /**
   * Start the enhanced idle animation system
   */
  start(applyBlendshapes: (blendshapes: Record<string, number>) => void): void {
    if (this.state.isActive) return;

    this.applyBlendshapes = applyBlendshapes;
    this.state.isActive = true;
    this.startTime = performance.now();
    this.breathCycleStart = this.startTime;
    this.nextBlinkTime = this.startTime + this.getRandomBlinkInterval();
    this.state.lastMicroExpressionTime = this.startTime;

    console.log('[EnhancedIdle] Started with pattern:', this.currentBreathingPattern);
    this.animate();
  }

  /**
   * Stop the enhanced idle animation system
   */
  stop(): void {
    this.state.isActive = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.applyBlendshapes = null;
    console.log('[EnhancedIdle] Stopped');
  }

  /**
   * Change breathing pattern dynamically
   */
  setBreathingPattern(pattern: BreathingPattern): void {
    this.currentBreathingPattern = pattern;
    this.breathCycleStart = performance.now();
    this.breathingVariation = (Math.random() - 0.5) * 0.2; // ±10% variation
    console.log('[EnhancedIdle] Breathing pattern changed to:', pattern);
  }

  /**
   * Set intensity multiplier (for mood-based adjustments)
   */
  setIntensity(intensity: number): void {
    this.config.intensityMultiplier = Math.max(0, Math.min(2, intensity));
  }

  /**
   * Get current state for debugging/monitoring
   */
  getState(): IdleAnimationState {
    return { ...this.state };
  }

  /**
   * Main animation loop
   */
  private animate = (): void => {
    if (!this.state.isActive || !this.applyBlendshapes) return;

    const now = performance.now();
    const blendshapes: Record<string, number> = {};

    // Calculate breathing contribution
    if (this.config.enableBreathing) {
      const breathBlends = this.calculateBreathing(now);
      Object.assign(blendshapes, breathBlends);
    }

    // Calculate micro-expression contribution
    if (this.config.enableMicroExpressions) {
      const microBlends = this.calculateMicroExpression(now);
      Object.assign(blendshapes, microBlends);
    }

    // Calculate blink contribution
    if (this.config.enableNaturalBlinks) {
      const blinkBlends = this.calculateBlink(now);
      Object.assign(blendshapes, blinkBlends);
    }

    // Apply intensity multiplier
    for (const key of Object.keys(blendshapes)) {
      blendshapes[key] *= this.config.intensityMultiplier;
    }

    Object.assign(blendshapes, sampleProceduralIdleNoise(now / 1000, 'neutral', this.config.intensityMultiplier));

    // Apply blendshapes
    this.applyBlendshapes(blendshapes);

    // Continue animation loop
    this.animationFrameId = requestAnimationFrame(this.animate);
  };

  /**
   * Calculate breathing blendshapes for current time
   */
  private calculateBreathing(now: number): Record<string, number> {
    const pattern = BREATHING_PATTERNS[this.currentBreathingPattern];
    const cycleTime = (now - this.breathCycleStart) / 1000;
    const adjustedCycleDuration = pattern.cycleDuration * (1 + this.breathingVariation);
    const phase = (cycleTime % adjustedCycleDuration) / adjustedCycleDuration;

    this.state.currentBreathPhase = phase;

    // Calculate breath intensity based on phase
    let breathIntensity: number;

    const inhaleEnd = pattern.inhaleRatio;
    const holdEnd = inhaleEnd + pattern.holdRatio;
    const exhaleEnd = 1.0;

    if (phase < inhaleEnd) {
      // Inhale phase - ease in
      const inhaleProgress = phase / inhaleEnd;
      breathIntensity = this.easeInOutSine(inhaleProgress) * pattern.peakIntensity;
    } else if (phase < holdEnd) {
      // Hold at peak
      breathIntensity = pattern.peakIntensity;
    } else {
      // Exhale phase - ease out
      const exhaleProgress = (phase - holdEnd) / (exhaleEnd - holdEnd);
      breathIntensity = (1 - this.easeInOutSine(exhaleProgress)) * pattern.peakIntensity;
    }

    this.state.breathingIntensity = breathIntensity;

    // Apply breathing to blendshapes
    const blendshapes: Record<string, number> = {};
    for (const [name, range] of Object.entries(pattern.blendshapes)) {
      blendshapes[name] = range.min + (range.max - range.min) * breathIntensity;
    }

    // Start new cycle with variation
    if (cycleTime >= adjustedCycleDuration) {
      this.breathCycleStart = now;
      this.breathingVariation = (Math.random() - 0.5) * 0.2;
    }

    return blendshapes;
  }

  /**
   * Calculate micro-expression blendshapes
   */
  private calculateMicroExpression(now: number): Record<string, number> {
    const blendshapes: Record<string, number> = {};

    // Check if we should start a new micro-expression
    const timeSinceLastMicro = (now - this.state.lastMicroExpressionTime) / 1000;
    const frequency = this.config.microExpressionFrequency * (0.7 + Math.random() * 0.6);

    if (!this.currentMicroExpression && timeSinceLastMicro > frequency) {
      // Select a random micro-expression (excluding blink types if blinking is handled separately)
      const expression = this.selectRandomMicroExpression();
      if (expression && expression !== 'blink' && expression !== 'doubleBlink' && expression !== 'halfBlink') {
        this.currentMicroExpression = {
          type: expression,
          startTime: now,
          frames: MICRO_EXPRESSIONS[expression].frames
        };
        this.state.currentMicroExpression = expression;
      }
      this.state.lastMicroExpressionTime = now;
    }

    // Animate current micro-expression
    if (this.currentMicroExpression) {
      const elapsed = (now - this.currentMicroExpression.startTime) / 1000;
      const expr = MICRO_EXPRESSIONS[this.currentMicroExpression.type];

      if (elapsed >= expr.duration) {
        // Expression finished
        this.currentMicroExpression = null;
        this.state.currentMicroExpression = null;
      } else {
        // Interpolate between frames
        const frameBlends = this.interpolateFrames(this.currentMicroExpression.frames, elapsed);
        Object.assign(blendshapes, frameBlends);
      }
    }

    return blendshapes;
  }

  /**
   * Calculate blink blendshapes
   */
  private calculateBlink(now: number): Record<string, number> {
    const blendshapes: Record<string, number> = {};

    // Check if we should start a blink
    if (!this.isBlinking && now >= this.nextBlinkTime) {
      this.isBlinking = true;
      this.blinkStartTime = now;
      this.state.lastBlinkTime = now;

      // Randomly select blink type
      const blinkRoll = Math.random() * 100;
      let blinkType: MicroExpressionType = 'blink';
      if (blinkRoll < 8) {
        blinkType = 'doubleBlink';
      } else if (blinkRoll < 20) {
        blinkType = 'halfBlink';
      }

      this.currentMicroExpression = {
        type: blinkType,
        startTime: now,
        frames: MICRO_EXPRESSIONS[blinkType].frames
      };
    }

    // Animate blink
    if (this.isBlinking && this.currentMicroExpression) {
      const elapsed = (now - this.blinkStartTime) / 1000;
      const blinkExpr = MICRO_EXPRESSIONS[this.currentMicroExpression.type];

      if (elapsed >= blinkExpr.duration) {
        // Blink finished
        this.isBlinking = false;
        this.nextBlinkTime = now + this.getRandomBlinkInterval();
        this.currentMicroExpression = null;
      } else {
        // Interpolate blink frames
        const frameBlends = this.interpolateFrames(this.currentMicroExpression.frames, elapsed);
        Object.assign(blendshapes, frameBlends);
      }
    }

    return blendshapes;
  }

  /**
   * Interpolate between animation frames
   */
  private interpolateFrames(frames: BlendshapeFrame[], time: number): Record<string, number> {
    if (frames.length === 0) return {};

    // Find surrounding frames
    let prevFrame = frames[0];
    let nextFrame = frames[frames.length - 1];

    for (let i = 0; i < frames.length - 1; i++) {
      if (time >= frames[i].time && time <= frames[i + 1].time) {
        prevFrame = frames[i];
        nextFrame = frames[i + 1];
        break;
      }
    }

    // Calculate interpolation factor
    const duration = nextFrame.time - prevFrame.time;
    const t = duration > 0 ? (time - prevFrame.time) / duration : 0;
    const easedT = this.easeInOutQuad(Math.max(0, Math.min(1, t)));

    // Interpolate blendshapes
    const result: Record<string, number> = {};
    const allKeys = Array.from(new Set([
      ...Object.keys(prevFrame.blendshapes),
      ...Object.keys(nextFrame.blendshapes)
    ]));

    for (const key of allKeys) {
      const prevVal = prevFrame.blendshapes[key] ?? 0;
      const nextVal = nextFrame.blendshapes[key] ?? 0;
      result[key] = prevVal + (nextVal - prevVal) * easedT;
    }

    return result;
  }

  /**
   * Select a random micro-expression based on probability weights
   */
  private selectRandomMicroExpression(): MicroExpressionType | null {
    const expressions = Object.entries(MICRO_EXPRESSIONS);
    const totalWeight = expressions.reduce((sum, [, expr]) => sum + expr.probability, 0);
    let random = Math.random() * totalWeight;

    for (const [type, expr] of expressions) {
      random -= expr.probability;
      if (random <= 0) {
        return type as MicroExpressionType;
      }
    }

    return 'blink'; // fallback
  }

  /**
   * Get random interval for next blink
   */
  private getRandomBlinkInterval(): number {
    const min = this.config.blinkFrequencyMin * 1000;
    const max = this.config.blinkFrequencyMax * 1000;
    return min + Math.random() * (max - min);
  }

  /**
   * Easing functions
   */
  private easeInOutSine(t: number): number {
    return -(Math.cos(Math.PI * t) - 1) / 2;
  }

  private easeInOutQuad(t: number): number {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.stop();
  }
}

/**
 * Factory function to create enhanced idle controller
 */
export function createEnhancedIdleController(
  config?: EnhancedIdleConfig
): EnhancedIdleController {
  return new EnhancedIdleController(config);
}

/**
 * Utility to get blendshapes for a specific mood
 * Returns appropriate breathing pattern and intensity
 */
export function getIdleConfigForMood(mood: string): Partial<EnhancedIdleConfig> {
  switch (mood.toLowerCase()) {
    case 'happy':
    case 'excited':
      return {
        breathingPattern: 'attentive',
        microExpressionFrequency: 6,
        intensityMultiplier: 1.2
      };
    case 'calm':
    case 'relaxed':
      return {
        breathingPattern: 'relaxed',
        microExpressionFrequency: 10,
        intensityMultiplier: 0.8
      };
    case 'thinking':
    case 'focused':
      return {
        breathingPattern: 'subtle',
        microExpressionFrequency: 12,
        intensityMultiplier: 0.9
      };
    case 'surprised':
    case 'alert':
      return {
        breathingPattern: 'deep',
        microExpressionFrequency: 5,
        intensityMultiplier: 1.3
      };
    default:
      return {
        breathingPattern: 'relaxed',
        microExpressionFrequency: 8,
        intensityMultiplier: 1.0
      };
  }
}

function hashNoise(t: number, seed: number): number {
  const x = Math.sin(t * 12.9898 + seed * 78.233) * 43758.5453;
  return (x - Math.floor(x)) * 2 - 1;
}

/**
 * Non-repeating idle micro-motion. Amplitudes stay tiny so this layers
 * additively over JSON idle clips / mood overlays without looking drunk.
 * Mood weights: concerned/negative raise brows and drop smile; positive
 * does the reverse; confused adds a slight left/right brow split.
 */
export function sampleProceduralIdleNoise(
  timeSec: number,
  mood: string = 'neutral',
  intensity: number = 1
): Record<string, number> {
  const t = timeSec;
  const scale = Math.max(0, Math.min(2, intensity));
  let brow = 1;
  let smile = 1;
  let browAsym = 0;
  const m = mood.toLowerCase();
  if (m === 'concerned' || m === 'negative' || m === 'frustrated') {
    brow = 2;
    smile = 0.3;
  } else if (m === 'positive' || m === 'celebration') {
    brow = 0.6;
    smile = 1.6;
  } else if (m === 'confused') {
    browAsym = 0.02;
  }

  return {
    browInnerUp: hashNoise(t * 0.3, 1) * 0.04 * brow * scale,
    browOuterUp_L: hashNoise(t * 0.25, 2) * 0.03 * brow * scale + browAsym,
    browOuterUp_R: hashNoise(t * 0.28, 3) * 0.03 * brow * scale - browAsym,
    eyeSquint_L: Math.max(0, hashNoise(t * 0.4, 4) * 0.02 * scale),
    eyeSquint_R: Math.max(0, hashNoise(t * 0.42, 5) * 0.02 * scale),
    mouthSmile_L: Math.max(0, hashNoise(t * 0.15, 6) * 0.02 * smile * scale),
    mouthSmile_R: Math.max(0, hashNoise(t * 0.17, 7) * 0.02 * smile * scale),
    jawOpen: Math.max(0, hashNoise(t * 0.2, 8) * 0.01 * scale),
  };
}
